/**
 * useMicRecorder.js
 *
 * Shared microphone recorder hook — used by the Soulumo Ask-AI
 * review, the personal Voice Library, and the admin Voice Bank
 * recorder. One implementation, so Safari quirks are fixed once.
 *
 * Returns:
 *   { state, blob, mime, error, elapsed, start, stop, reset }
 * where state ∈ "idle" | "recording" | "ready".
 */
import { useCallback, useEffect, useRef, useState } from "react";

function pickMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/mpeg",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch { /* older browsers throw */ }
  }
  return "";
}

export default function useMicRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const [state, setState] = useState("idle");
  const [blob, setBlob] = useState(null);
  const [mime, setMime] = useState("audio/webm");
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);

  // Tick a wall-clock while recording — used by UIs to show "0:07".
  useEffect(() => {
    if (state !== "recording") return;
    const startedAt = Date.now();
    const iv = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => clearInterval(iv);
  }, [state]);

  const start = useCallback(async () => {
    setError("");
    setBlob(null);
    setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const chosen = pickMime();
      const mr = chosen ? new MediaRecorder(stream, { mimeType: chosen })
                        : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || chosen || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setMime(type);
        setState("ready");
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setState("recording");
    } catch (e) {
      setError(e?.message || "mic_denied");
      setState("idle");
    }
  }, []);

  const stop = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setState("idle");
    setError("");
    setElapsed(0);
  }, []);

  return { state, blob, mime, error, elapsed, start, stop, reset };
}
